import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Sparkles, Lock, Globe, Pencil, Trash2, Award, ExternalLink,
  Github, FileText, Loader2, StickyNote,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useApi, fileUrl } from "@/lib/api";
import { SOURCE_META, gradientFor } from "@/lib/sourceMeta";
import EntryCard from "@/components/EntryCard";
import { toast } from "sonner";

export default function EntryDetail() {
  const { id } = useParams();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: entry, isLoading } = useQuery({
    queryKey: ["entry", id],
    queryFn: () => api.get(`/entries/${id}`),
  });
  const { data: related } = useQuery({
    queryKey: ["related", id],
    queryFn: () => api.get(`/entries/${id}/related`),
  });

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  if (isLoading || !entry) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-32 bg-secondary rounded-full" />
        <div className="grid lg:grid-cols-[1.4fr,1fr] gap-10">
          <div className="aspect-[4/3] rounded-3xl bg-secondary" />
          <div className="space-y-4">
            <div className="h-8 bg-secondary rounded w-3/4" />
            <div className="h-24 bg-secondary rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  const meta = SOURCE_META[entry.source_type] || SOURCE_META.website;
  const Icon = meta.icon;
  const img = fileUrl(entry.image_path);
  const isArchive = entry.type === "archive";

  return (
    <div data-testid="entry-detail-page" className="space-y-10">
      <button
        data-testid="back-button"
        onClick={() => navigate(isArchive ? "/archive" : "/brain")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid lg:grid-cols-[1.4fr,1fr] gap-10 items-start">
        <div className="space-y-8">
          {img ? (
            <img
              data-testid="entry-detail-image"
              src={img}
              alt={entry.title}
              className="w-full rounded-3xl border border-border shadow-[0_10px_30px_rgba(0,0,0,0.06)] object-cover"
            />
          ) : (
            <div
              data-testid="entry-detail-image"
              className={`aspect-[4/3] rounded-3xl bg-gradient-to-br ${gradientFor(entry.id)} flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.06)]`}
            >
              <Icon className="h-14 w-14 text-white/80" strokeWidth={1} />
            </div>
          )}

          {isArchive && (
            <div data-testid="portfolio-fields" className="space-y-6">
              {entry.description && (
                <section>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Description</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-stone-700">{entry.description}</p>
                </section>
              )}
              {entry.contribution && (
                <section>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">My contribution</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-stone-700">{entry.contribution}</p>
                </section>
              )}
              {entry.outcomes && (
                <section>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Outcomes &amp; impact</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-stone-700">{entry.outcomes}</p>
                </section>
              )}
              {!!entry.evidence_links?.length && (
                <section>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Evidence</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {entry.evidence_links.map((l, i) => (
                      <a
                        key={i}
                        data-testid={`evidence-link-${i}`}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-medium hover:border-primary/50 hover:text-primary transition-[color,border-color]"
                      >
                        {(l.url || "").includes("github") ? (
                          <Github className="h-3.5 w-3.5" />
                        ) : (l.url || "").includes("arxiv") || (l.url || "").includes("doi") ? (
                          <FileText className="h-3.5 w-3.5" />
                        ) : (
                          <ExternalLink className="h-3.5 w-3.5" />
                        )}
                        {l.label || "Link"}
                      </a>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        <div className="space-y-5 lg:sticky lg:top-24">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 bg-secondary rounded-full px-3 py-1.5">
              <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} /> {meta.label}
            </span>
            {isArchive && (
              <span data-testid="achievement-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 rounded-full px-3 py-1.5">
                <Award className="h-3.5 w-3.5" /> Achievement
              </span>
            )}
            <span
              data-testid="visibility-chip"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 border border-border rounded-full px-3 py-1.5"
            >
              {entry.visibility === "public" ? <Globe className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
              {entry.visibility === "public" ? "Public" : "Private"}
            </span>
          </div>

          <h1 data-testid="entry-detail-title" className="font-serif text-3xl md:text-4xl leading-tight tracking-tight">
            {entry.title}
          </h1>

          {entry.site_name && <p className="text-xs text-stone-400">{entry.site_name}</p>}

          {entry.url && (
            <a
              data-testid="original-url-link"
              href={entry.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex max-w-full items-center gap-1.5 text-sm font-medium text-primary hover:opacity-80 transition-opacity"
            >
              <span className="truncate">{entry.url}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
            </a>
          )}

          {entry.summary && (
            <div
              data-testid="entry-ai-summary"
              className="rounded-2xl border border-border bg-card p-5 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
            >
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="h-3.5 w-3.5" /> AI summary
              </p>
              <p className="mt-2 text-sm leading-relaxed text-stone-700">{entry.summary}</p>
            </div>
          )}

          <NoteBlock entry={entry} onSaved={() => qc.invalidateQueries({ queryKey: ["entry", id] })} />

          {!!entry.tags?.length && (
            <div data-testid="entry-tags" className="flex flex-wrap gap-2">
              {entry.tags.map((t) => (
                <Link
                  key={t}
                  to={`/brain?tag=${encodeURIComponent(t)}`}
                  className="rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  #{t}
                </Link>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-2.5 pt-1">
            {!isArchive && (
              <button
                data-testid="convert-to-achievement-button"
                onClick={async () => {
                  try {
                    await api.post(`/entries/${id}/convert`);
                    qc.invalidateQueries();
                    toast.success("Turned into an achievement — fill in your contribution and outcomes");
                  } catch {
                    toast.error("Could not convert");
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold shadow-md shadow-primary/20 hover:opacity-90 transition-opacity"
              >
                <Award className="h-4 w-4" /> Turn into achievement
              </button>
            )}
            <div className="flex gap-2.5">
              <button
                data-testid="entry-edit-button"
                onClick={() => setEditOpen(true)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-stone-400 transition-colors"
              >
                <Pencil className="h-4 w-4" /> Edit
              </button>
              <button
                data-testid="entry-delete-button"
                onClick={() => setConfirmDel(true)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-destructive hover:bg-destructive/5 transition-colors"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {!!related?.length && (
        <section data-testid="related-section">
          <h2 className="font-serif text-2xl tracking-tight mb-5">Related discoveries</h2>
          <div className="columns-2 lg:columns-4 gap-5">
            {related.map((e) => (
              <EntryCard key={e.id} entry={e} className="text-sm" />
            ))}
          </div>
        </section>
      )}

      {editOpen && (
        <EditDialog
          entry={entry}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            qc.invalidateQueries();
            setEditOpen(false);
          }}
        />
      )}

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-xl">Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This can't be undone. "{entry.title}" will leave your archive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="entry-delete-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction
              data-testid="entry-delete-confirm"
              onClick={async () => {
                try {
                  await api.del(`/entries/${id}`);
                  toast.success("Deleted");
                  navigate(isArchive ? "/archive" : "/brain");
                } catch {
                  toast.error("Could not delete");
                }
              }}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function NoteBlock({ entry, onSaved }) {
  const api = useApi();
  const [draft, setDraft] = useState(entry.note || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!editing) {
    return (
      <div
        data-testid="entry-note"
        onClick={() => setEditing(true)}
        className="rounded-2xl border border-dashed border-border bg-secondary/40 p-5 cursor-pointer hover:border-primary/40 transition-[border-color]"
      >
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
          <StickyNote className="h-3.5 w-3.5" /> Personal note
        </p>
        <p className="mt-2 text-sm leading-relaxed text-stone-700">
          {entry.note || "Tap to add your own thinking — what this means to you, what to do with it…"}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-400">
        <StickyNote className="h-3.5 w-3.5" /> Personal note
      </p>
      <Textarea
        data-testid="entry-note-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={4}
        className="mt-2"
        autoFocus
      />
      <div className="mt-3 flex gap-2 justify-end">
        <button
          data-testid="entry-note-cancel"
          onClick={() => {
            setDraft(entry.note || "");
            setEditing(false);
          }}
          className="text-xs font-medium text-muted-foreground hover:text-foreground px-3 py-2"
        >
          Cancel
        </button>
        <button
          data-testid="entry-note-save"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await api.patch(`/entries/${entry.id}`, { note: draft });
              toast.success("Note saved");
              setEditing(false);
              onSaved();
            } catch {
              toast.error("Could not save note");
            } finally {
              setSaving(false);
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-90"
        >
          {saving && <Loader2 className="h-3 w-3 animate-spin" />} Save note
        </button>
      </div>
    </div>
  );
}

function EditDialog({ entry, onClose, onSaved }) {
  const api = useApi();
  const isArchive = entry.type === "archive";
  const [form, setForm] = useState({
    title: entry.title || "",
    tags: (entry.tags || []).join(", "),
    note: entry.note || "",
    description: entry.description || "",
    contribution: entry.contribution || "",
    outcomes: entry.outcomes || "",
    evidence: (entry.evidence_links || []).length ? entry.evidence_links : [],
    public: entry.visibility === "public",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: form.title,
        tags: form.tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean).slice(0, 8),
        note: form.note,
        visibility: form.public ? "public" : "private",
      };
      if (isArchive) {
        body.description = form.description;
        body.contribution = form.contribution;
        body.outcomes = form.outcomes;
        body.evidence_links = form.evidence.filter((l) => l.url || l.label);
      }
      await api.patch(`/entries/${entry.id}`, body);
      toast.success("Saved");
      onSaved();
    } catch {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-entry-dialog" className="max-w-lg rounded-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Title">
            <Input data-testid="edit-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} />
          </Field>
          <Field label="Tags (comma separated)">
            <Input data-testid="edit-tags-input" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="machine-learning, design" />
          </Field>
          <Field label="Personal note">
            <Textarea data-testid="edit-note-input" value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} />
          </Field>

          {isArchive && (
            <>
              <Field label="Project description">
                <Textarea data-testid="edit-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} />
              </Field>
              <Field label="My contribution">
                <Textarea data-testid="edit-contribution-input" value={form.contribution} onChange={(e) => set("contribution", e.target.value)} rows={2} />
              </Field>
              <Field label="Outcomes & impact">
                <Textarea data-testid="edit-outcomes-input" value={form.outcomes} onChange={(e) => set("outcomes", e.target.value)} rows={2} />
              </Field>
              <Field label="Evidence links">
                <div className="space-y-2">
                  {form.evidence.map((l, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        data-testid={`evidence-label-${i}`}
                        placeholder="Label"
                        value={l.label || ""}
                        onChange={(e) => set("evidence", form.evidence.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                        className="w-1/3"
                      />
                      <Input
                        data-testid={`evidence-url-${i}`}
                        placeholder="https://…"
                        value={l.url || ""}
                        onChange={(e) => set("evidence", form.evidence.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                        className="flex-1"
                      />
                      <button
                        data-testid={`evidence-remove-${i}`}
                        onClick={() => set("evidence", form.evidence.filter((_, j) => j !== i))}
                        className="text-xs text-stone-400 hover:text-destructive px-2"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    data-testid="evidence-add-button"
                    onClick={() => set("evidence", [...form.evidence, { label: "", url: "" }])}
                    className="text-xs font-semibold text-primary hover:opacity-80"
                  >
                    + Add link
                  </button>
                </div>
              </Field>
            </>
          )}

          <div className="flex items-center justify-between rounded-2xl border border-border p-4">
            <div>
              <p className="text-sm font-semibold">Public</p>
              <p className="text-[11px] text-muted-foreground">Off means only you can see this entry.</p>
            </div>
            <Switch
              data-testid="edit-visibility-switch"
              checked={form.public}
              onCheckedChange={(v) => set("public", v)}
            />
          </div>

          <Button
            data-testid="edit-save-button"
            onClick={save}
            disabled={saving || !form.title.trim()}
            className="w-full rounded-full py-6 font-semibold"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
